import type { Permission } from '@netdeck/contracts';
import type { IconName } from './Icon';

/**
 * Разделы меню.
 *
 * Отдельным модулем, а не рядом с `AppShell`: файл, экспортирующий
 * и компонент, и обычное значение, Vite не умеет обновлять горячей
 * заменой — он перезагружает его целиком вместе со всем, что от него
 * зависит. На глаз это секундное замирание оболочки на ровном месте,
 * и происходит оно при каждой правке.
 */
export interface NavItem {
  id: string;
  /** Ключ подписи в словаре: раздел называется на языке того, кто смотрит. */
  labelKey: string;
  /**
   * Якорь пункта. Обязателен: свёрнутое меню — это одни иконки, и пункт
   * без глифа в нём попросту исчезает.
   */
  icon: IconName;
  /** Право, без которого пункт не показывается. */
  permission?: Permission;
  /**
   * Пункт виден при наличии **любого** из перечисленных прав.
   *
   * Нужен разделам, собранным из блоков с разными правами: обзор показывает
   * сеть по `node:read` и платформу по `site:read`, и человеку с одним
   * из двух он показывает половину, а не отказ.
   */
  anyPermission?: Permission[];
  /** Счётчик справа — например, число событий. */
  badge?: number;
  /** Раздел пока не реализован: пункт виден, но неактивен. */
  planned?: boolean;
}

export interface NavGroup {
  /** Ключ заголовка группы в словаре. */
  titleKey: string;
  items: NavItem[];
}

/**
 * Разделы меню по макету NetDeck.html.
 *
 * Пункты будущих фаз показаны неактивными намеренно: инженер должен видеть,
 * из чего состоит платформа, а не гадать, куда делась видеостена. §12 требует
 * от пустых состояний приглашения к действию — здесь это честное «пока нет».
 */
export const NAV_GROUPS: NavGroup[] = [
  {
    titleKey: 'nav.group.monitoring',
    items: [
      /*
        Обзор стоит первым в списке, но стартовой страницей остаётся карта
        (§11, фаза 9, задача 1): обзор отвечает на вопрос «как дела вообще»,
        а рабочий день проходит на карте.

        Право не одно намеренно: блока здесь два, и у каждого своё — сеть
        требует `node:read`, платформа `site:read`. Пункт виден тому, у кого
        есть хотя бы одно из них, и показывает ровно тот блок, на который
        право есть.
      */
      {
        id: 'overview',
        labelKey: 'nav.overview',
        icon: 'navOverview',
        anyPermission: ['node:read', 'site:read'],
      },
      { id: 'map', labelKey: 'nav.map', icon: 'navMap', permission: 'node:read' },
      { id: 'nodes', labelKey: 'nav.nodes', icon: 'navNodes', permission: 'node:read' },
      { id: 'events', labelKey: 'nav.events', icon: 'navEvents', permission: 'monitor:read' },
      // Замер производительности карты (§14). Виден только в разработке:
      // это инструмент, а не возможность платформы.
      ...(import.meta.env.DEV
        ? [
            {
              id: 'perf',
              labelKey: 'nav.perf',
              icon: 'zoomArea' as IconName,
              permission: 'node:read' as Permission,
            },
          ]
        : []),
    ],
  },
  {
    titleKey: 'nav.group.access',
    items: [
      { id: 'vnc', labelKey: 'nav.vnc', icon: 'navVnc', permission: 'vnc:connect' },
      { id: 'ssh', labelKey: 'nav.ssh', icon: 'navSsh', permission: 'ssh:connect' },
      { id: 'video', labelKey: 'nav.video', icon: 'navVideo', permission: 'camera:view' },
      { id: 'archive', labelKey: 'nav.archive', icon: 'navArchive', permission: 'camera:archive' },
      /*
        Сессии видны и тому, у кого только терминал: экран показывает и те,
        и другие — таблица одна, предел одновременных сессий тоже общий.
      */
      {
        id: 'sessions',
        labelKey: 'nav.sessions',
        icon: 'navSessions',
        anyPermission: ['vnc:connect', 'ssh:connect'],
      },
    ],
  },
  {
    titleKey: 'nav.group.administration',
    items: [
      { id: 'sites', labelKey: 'nav.sites', icon: 'navSites', permission: 'site:read' },
      {
        id: 'credentials',
        labelKey: 'nav.credentials',
        icon: 'key',
        permission: 'credential:write',
      },
      { id: 'agents', labelKey: 'nav.agents', icon: 'navAgents', permission: 'site:read' },
      {
        id: 'notifications',
        labelKey: 'nav.notifications',
        icon: 'navNotifications',
        permission: 'org:admin',
      },
      { id: 'users', labelKey: 'nav.users', icon: 'navUsers', permission: 'org:admin' },
      { id: 'audit', labelKey: 'nav.audit', icon: 'navAudit', permission: 'audit:read' },
    ],
  },
];

/**
 * Раздел, с которого начинается работа, если человек ещё никуда не ходил.
 *
 * Карта, а не «Площадки»: §11 (фаза 9, задача 1) прямо оставляет стартовой
 * страницей карту — рабочий день проходит на ней, а площадки настраивают
 * однажды и больше туда не заходят.
 */
export const DEFAULT_SECTION = 'map';

/**
 * Виден ли пункт при данных правах.
 *
 * `permission` требуется целиком, `anyPermission` — хотя бы одно из списка:
 * раздел из нескольких блоков с разными правами показывается тому, кому
 * положен хоть один блок, а не только тому, кому положены все.
 */
export function isVisible(item: NavItem, granted: ReadonlySet<string>): boolean {
  if (item.permission && !granted.has(item.permission)) {
    return false;
  }

  if (item.anyPermission && !item.anyPermission.some((permission) => granted.has(permission))) {
    return false;
  }

  return true;
}

/**
 * Какой раздел показать, если хотели показать `id`.
 *
 * Правила у прав и у меню должны быть одни: показать раздел, которого нет
 * в меню, значит открыть человеку экран, куда он не может ни вернуться,
 * ни уйти осмысленно. Поэтому запомненный раздел проходит ту же проверку,
 * что и пункт меню, а не годится — уступает место карте, а если и карты
 * человеку не положено (наблюдатель за одними площадками), первому пункту,
 * который ему виден вообще.
 */
export function resolveSection(id: string | null, granted: ReadonlySet<string>): string {
  const items = NAV_GROUPS.flatMap((group) => group.items).filter(
    (item) => !item.planned && isVisible(item, granted),
  );

  const wanted = items.find((item) => item.id === id);
  const fallback = items.find((item) => item.id === DEFAULT_SECTION) ?? items[0];

  return wanted?.id ?? fallback?.id ?? DEFAULT_SECTION;
}
